import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { hasCardAccess, isHomeSectionVisible } from '../utils/permissionHelper';
import UserProfileButton from './common/UserProfileButton';
import LogoutButton from './common/LogoutButton';
import axiosInstance from '../api/axiosConfig';
import {
    HiOutlineCalendar,
    HiOutlineClipboardCheck,
    HiOutlineBookOpen,
    HiOutlineRefresh
} from 'react-icons/hi';
import { useAppShell } from './Layout/AppShell';
import { HOME_SECTION_TODAY_TASKS, HOME_SECTION_DOC_LOG } from '../constants/homeSections';
import './PurposeSelection.css';
import receptionBg from '../assets/images/backgrounds/reception-bg.png';

/**
 * Home.
 *
 * Just the header: who you are, what day it is, and the day's headline numbers.
 * Navigation lives in the shell's left sidebar, which is on every page — so
 * there is nothing to repeat here.
 *
 * Permissions and the pending counts come from the shell (`useAppShell`) rather
 * than being fetched again: the sidebar badge and the tile below it must be the
 * same number, and they cannot be if two components ask separately.
 */
const PurposeSelection = () => {
    const navigate = useNavigate();
    const { user } = useAuth();
    const { permissions, counts, countLoading, refreshCount, isAdmin } = useAppShell() || {};

    // Memoised: a fresh object literal every render would re-run everything
    // downstream that depends on it.
    const userPermissions = useMemo(
        () => permissions || { cards: [], actions: [] },
        [permissions]
    );

    // Tasks due today is the one number no other screen needs, so it is fetched
    // here instead of in the shell.
    const [todayCount, setTodayCount] = useState(0);
    const [todayLoading, setTodayLoading] = useState(true);

    const canSeeTasks = isAdmin || hasCardAccess(userPermissions, 'task-management');
    const canSeeDocLog = isAdmin || hasCardAccess(userPermissions, 'document-log-book');

    // Titles of the documents actually waiting on this user — the tile's count
    // says how many, this fills in what. Fetched separately from the shell's
    // count because the count source only asks for a total (limit=1).
    const [pendingDocTitles, setPendingDocTitles] = useState([]);
    const [tickerIndex, setTickerIndex] = useState(0);

    const fetchPendingDocTitles = useCallback(async () => {
        if (!canSeeDocLog) return;

        try {
            const response = await axiosInstance.get('/api/document-log-book/pending/my?limit=6');
            const titles = (response.data?.data || [])
                .map(entry => entry.documents?.trim())
                .filter(Boolean);
            setPendingDocTitles(titles);
            setTickerIndex(0);
        } catch (error) {
            console.error('Error fetching pending document titles:', error);
        }
    }, [canSeeDocLog]);

    useEffect(() => {
        fetchPendingDocTitles();
    }, [fetchPendingDocTitles]);

    // Cycle to the next title every few seconds; the fade in/out is a CSS
    // animation keyed to the index so it restarts on every change.
    useEffect(() => {
        if (pendingDocTitles.length < 2) return undefined;

        const timer = setInterval(() => {
            setTickerIndex(i => (i + 1) % pendingDocTitles.length);
        }, 3200);
        return () => clearInterval(timer);
    }, [pendingDocTitles.length]);

    const fetchTodayCount = useCallback(async () => {
        if (!canSeeTasks) return;

        try {
            setTodayLoading(true);
            // Unfinished and due today. "Today" is decided by the server on a
            // Qatar day — see utils/qatarTime.js.
            const response = await axiosInstance.get(
                '/api/tasks/my-tasks?dueToday=true&status=todo,in-progress,review&limit=1'
            );
            setTodayCount(response.data?.totalTasks || 0);
        } catch (error) {
            console.error('Error fetching today\'s tasks:', error);
        } finally {
            setTodayLoading(false);
        }
    }, [canSeeTasks]);

    useEffect(() => {
        fetchTodayCount();
    }, [fetchTodayCount]);

    // ── Header tiles ────────────────────────────────────────────────────────
    // A tile shows when the user holds the card for it AND an admin has not
    // hidden it in User Management. Visibility is read from the HIDDEN list, so
    // a user with nothing configured sees both. Hiding a tile is presentation
    // only — the module behind it is still gated by its card on the server.
    const headerTiles = useMemo(() => ([
        {
            id: HOME_SECTION_TODAY_TASKS,
            label: "Today's Tasks",
            value: todayCount,
            hint: 'Due today',
            icon: HiOutlineClipboardCheck,
            color: '#10b981',
            path: '/tasks',
            loading: todayLoading,
            onRefresh: fetchTodayCount,
            visible: canSeeTasks && isHomeSectionVisible(userPermissions, HOME_SECTION_TODAY_TASKS)
        },
        {
            id: HOME_SECTION_DOC_LOG,
            label: 'Document Log Book',
            // The same count the sidebar badge shows, from the shell.
            value: counts?.['document-log-book'] || 0,
            hint: 'Pending acknowledgement',
            icon: HiOutlineBookOpen,
            color: '#8b5cf6',
            path: '/document-log-book',
            loading: !!countLoading?.['document-log-book'],
            onRefresh: () => {
                refreshCount?.('document-log-book');
                fetchPendingDocTitles();
            },
            ticker: pendingDocTitles.length > 0
                ? { text: pendingDocTitles[tickerIndex % pendingDocTitles.length], key: tickerIndex }
                : null,
            visible: canSeeDocLog && isHomeSectionVisible(userPermissions, HOME_SECTION_DOC_LOG)
        }
    ].filter(tile => tile.visible)),
        [canSeeTasks, canSeeDocLog, userPermissions, todayCount, todayLoading, fetchTodayCount,
            counts, countLoading, refreshCount, pendingDocTitles, tickerIndex, fetchPendingDocTitles]);

    const greeting = useMemo(() => {
        const hour = new Date().getHours();
        if (hour < 12) return 'Good morning';
        if (hour < 17) return 'Good afternoon';
        return 'Good evening';
    }, []);

    const todayLabel = useMemo(() => new Date().toLocaleDateString(undefined, {
        weekday: 'long', day: 'numeric', month: 'long', year: 'numeric'
    }), []);

    return (
        <div className="purpose-container" style={{ backgroundImage: `url(${receptionBg})` }}>
            <div className="purpose-content">
                <header className="home-hero">
                    <div className="home-hero-top">
                        <div className="home-hero-greeting">
                            <div>
                                <span className="home-greeting-eyebrow">{greeting},</span>
                                <h1>Hello, {user?.fullName || user?.username}! <span className="home-wave-emoji" role="img" aria-label="wave">&#128075;</span></h1>
                                <p className="home-hero-subtitle">Manage your office operations efficiently from one place.</p>
                            </div>
                        </div>

                        <div className="home-hero-aside">
                            <div className="purpose-header-actions">
                                <UserProfileButton />
                                <LogoutButton variant="secondary" />
                            </div>
                            <span className="home-hero-date">
                                <HiOutlineCalendar size={16} />
                                {todayLabel}
                            </span>
                        </div>
                    </div>

                    {headerTiles.length > 0 && (
                        <div className="home-hero-tiles">
                            {headerTiles.map(tile => {
                                const Icon = tile.icon;
                                return (
                                    /* A div, not a button: the reload below is a real
                                       button and a button cannot nest inside one. */
                                    <div
                                        key={tile.id}
                                        className="home-hero-tile"
                                        style={{ '--tile-color': tile.color }}
                                        onClick={() => navigate(tile.path)}
                                        role="button"
                                        tabIndex={0}
                                        onKeyDown={(e) => {
                                            if (e.key === 'Enter' || e.key === ' ') {
                                                e.preventDefault();
                                                navigate(tile.path);
                                            }
                                        }}
                                    >
                                        <span className="home-hero-tile-icon">
                                            <Icon size={22} />
                                        </span>
                                        <span className="home-hero-tile-body">
                                            <span className="home-hero-tile-label">{tile.label}</span>
                                            <span className="home-hero-tile-value">
                                                {tile.loading ? '—' : tile.value}
                                            </span>
                                            <span className="home-hero-tile-hint">{tile.hint}</span>
                                            {tile.ticker && (
                                                <span key={tile.ticker.key} className="home-hero-tile-ticker">
                                                    {tile.ticker.text}
                                                </span>
                                            )}
                                        </span>
                                        <button
                                            type="button"
                                            className="home-hero-tile-refresh"
                                            onClick={(e) => {
                                                // Reload the count without opening the module.
                                                e.stopPropagation();
                                                tile.onRefresh();
                                            }}
                                            disabled={tile.loading}
                                            title={`Refresh ${tile.label}`}
                                            aria-label={`Refresh ${tile.label}`}
                                        >
                                            <HiOutlineRefresh size={16} className={tile.loading ? 'spinning' : ''} />
                                        </button>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </header>

                <section className="home-coming-soon">
                    <span className="home-coming-soon-badge">Coming Soon</span>
                    <h2>More insights, right here on your home page</h2>
                    <p>We're working on bringing more at-a-glance information to this space.</p>
                </section>
            </div>
        </div>
    );
};

export default PurposeSelection;
